import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import {
  promptRefinementService,
  type RefinementSession,
  type RunErrorAnalysis,
  type CandidatePromptResponse,
  type CandidateSchemaResponse,
} from '../../services/promptRefinementService';
import { StepAnalyse } from '../../components/benchmark/optimisation/StepAnalyse';
import { StepSuggestions } from '../../components/benchmark/optimisation/StepSuggestions';
import { StepCandidat } from '../../components/benchmark/optimisation/StepCandidat';
import { PageContainer } from '../../components/layout';

const STEPS = [
  { key: 'analyse', label: 'Analyse' },
  { key: 'suggestions', label: 'Suggestions' },
  { key: 'candidat', label: 'Candidats' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

function getStepIndex(key: StepKey): number {
  return STEPS.findIndex((s) => s.key === key);
}

export function OptimisationSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const isNew = sessionId === 'new';

  const [session, setSession] = useState<RefinementSession | null>(null);
  const [activeStep, setActiveStep] = useState<StepKey>('analyse');
  const [loading, setLoading] = useState(false);

  // Note and tags (always visible and editable)
  const [note, setNote] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Data passed between steps
  const [errorAnalysis, setErrorAnalysis] = useState<RunErrorAnalysis | null>(null);
  const [candidatePrompt, setCandidatePrompt] = useState<CandidatePromptResponse | null>(null);
  const [candidateSchema, setCandidateSchema] = useState<CandidateSchemaResponse | null>(null);

  // Load existing session
  useEffect(() => {
    if (!isNew && sessionId) {
      loadSession(sessionId);
    }
  }, [sessionId, isNew]);

  const loadSession = async (id: string) => {
    setLoading(true);
    try {
      const data = await promptRefinementService.getSession(id);
      setSession(data);

      // Auto-navigate to the appropriate step based on status
      if (data.status === 'applied') {
        setActiveStep('candidat');
      } else if (data.status === 'generated' || data.status === 'accepted') {
        setActiveStep('suggestions');
      } else {
        setActiveStep('analyse');
      }

      // Restore note/tags
      if (data.note) setNote(data.note);
      if (data.tags) setTags(data.tags);

      // Extract error analysis from session if available
      if (data.error_analysis) {
        setErrorAnalysis(data.error_analysis as unknown as RunErrorAnalysis);
      }
    } catch (err) {
      toast.error('Session introuvable');
      navigate('/benchmark/optimisation');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyseComplete = (analysis: RunErrorAnalysis, newSession: RefinementSession) => {
    setSession(newSession);
    setErrorAnalysis(analysis);
    setActiveStep('suggestions');
    // Update URL if this was a new session
    if (isNew) {
      navigate(`/benchmark/optimisation/${newSession.id}`, { replace: true });
    }
  };

  const handleSuggestionsComplete = (
    updatedSession: RefinementSession,
    candidatePromptResult: CandidatePromptResponse | CandidateSchemaResponse | null,
    candidateSchemaResult?: CandidateSchemaResponse | null,
  ) => {
    setSession(updatedSession);
    setCandidatePrompt(
      candidatePromptResult && !('fields_modified' in candidatePromptResult)
        ? candidatePromptResult as CandidatePromptResponse
        : null
    );
    setCandidateSchema(
      candidateSchemaResult || (candidatePromptResult && 'fields_modified' in candidatePromptResult
        ? candidatePromptResult as CandidateSchemaResponse
        : null)
    );
    setActiveStep('candidat');
  };

  const handleStepClick = (stepKey: StepKey) => {
    const targetIndex = getStepIndex(stepKey);
    const currentMaxIndex = getMaxAccessibleStepIndex();
    if (targetIndex <= currentMaxIndex) {
      setActiveStep(stepKey);
    }
  };

  const getMaxAccessibleStepIndex = (): number => {
    if (!session) return 0;
    switch (session.status) {
      case 'applied': return 2;
      case 'accepted':
      case 'generated': return 1;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/benchmark/optimisation')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold">
          {isNew ? 'Nouvelle session d\'optimisation' : 'Session d\'optimisation'}
        </h1>
        {session && (
          <span className="text-sm text-gray-500">
            ({session.id.slice(0, 8)})
          </span>
        )}
      </div>

      {/* Session metadata — always visible */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-start">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => {
                if (session?.id) {
                  promptRefinementService.patchMetadata(session.id, { note }).catch(() => {});
                }
              }}
              className="w-full border rounded px-3 py-1.5 text-sm"
              rows={1}
              placeholder="Description de cette session"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tags</label>
            <div className="flex items-center gap-2 flex-wrap">
              {tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      const newTags = tags.filter((t) => t !== tag);
                      setTags(newTags);
                      if (session?.id) {
                        promptRefinementService.patchMetadata(session.id, { tags: newTags }).catch(() => {});
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const trimmed = tagInput.trim();
                    if (trimmed && !tags.includes(trimmed)) {
                      const newTags = [...tags, trimmed];
                      setTags(newTags);
                      setTagInput('');
                      if (session?.id) {
                        promptRefinementService.patchMetadata(session.id, { tags: newTags }).catch(() => {});
                      }
                    }
                  }
                }}
                className="border rounded px-2 py-1 text-xs w-24"
                placeholder="+ tag"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Statut</label>
            <span className={`inline-flex px-2.5 py-1 rounded text-xs font-medium ${
              session?.status === 'applied' ? 'bg-green-100 text-green-700' :
              session?.status === 'accepted' ? 'bg-yellow-100 text-yellow-700' :
              session?.status === 'generated' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {session?.status === 'applied' ? 'Candidat créé' :
               session?.status === 'accepted' ? 'Acceptées' :
               session?.status === 'generated' ? 'Suggestions générées' :
               isNew ? 'Nouvelle' : 'En attente'}
            </span>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-stretch bg-white rounded-lg shadow-sm mb-6 overflow-hidden border border-gray-200">
        {STEPS.map((step, idx) => {
          const isActive = activeStep === step.key;
          const isAccessible = idx <= getMaxAccessibleStepIndex();
          const isCompleted = idx < getStepIndex(activeStep);

          return (
            <button
              key={step.key}
              onClick={() => handleStepClick(step.key)}
              disabled={!isAccessible}
              className={`flex-1 flex items-center gap-2 px-6 py-4 text-left transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : isAccessible
                  ? 'bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'
                  : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                isActive ? 'bg-white text-primary' : isCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
              }`}>
                {isCompleted ? '✓' : idx + 1}
              </span>
              <span className="font-medium">{step.label}</span>
              {idx < STEPS.length - 1 && (
                <span className={`ml-auto text-lg ${isActive ? 'text-white/50' : 'text-gray-300'}`}>&rsaquo;</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Step content */}
      {activeStep === 'analyse' && (
        <StepAnalyse
          session={session}
          onComplete={handleAnalyseComplete}
        />
      )}
      {activeStep === 'suggestions' && (
        <StepSuggestions
          session={session}
          errorAnalysis={errorAnalysis}
          onComplete={handleSuggestionsComplete}
        />
      )}
      {activeStep === 'candidat' && (
        <StepCandidat
          session={session}
          candidatePrompt={candidatePrompt}
          candidateSchemaFromParent={candidateSchema}
        />
      )}
    </PageContainer>
  );
}
